import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Icon } from '@bciti/types';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil, tap, map } from 'rxjs/operators';
import { State } from 'src/app/store';
import { profileNameSelector, profilePictureSelector } from 'src/app/user/store/user.selectors';
import { GetNavigationAction, SaveLanguageAction, SetLanguageAction } from '../../store/global.actions';
import { availableLanguagesSelector, languageSelector, mainNavigationItemsSelector, secondaryNavigationItemsSelector } from '../../store/global.selectors';
import { ReportContextService } from '../../services/report-problem/report-context.service';
import { ReportProblemDialogComponent, ReportProblemDialogData, REPORT_PROBLEM_DIALOG_ID } from '../dialogs/report-problem-dialog/report-problem-dialog.component';

@Component({
    selector: 'app-menu',
    templateUrl: './menu.component.html',
    styleUrls: ['./menu.component.scss'],
})
export class MenuComponent implements OnInit, OnDestroy {
    public destroy$ = new Subject();

    public menuOpen = true;

    public Icon = Icon;

    public name$ = this.store.select(profileNameSelector);

    public picture$ = this.store.select(profilePictureSelector);

    public mainItems$ = this.store.select(mainNavigationItemsSelector);

    public language$ = this.store.select(languageSelector);

    // TODO: consolidate notification nav items on the backend — remove this frontend filter
    public secItems$ = this.store.select(secondaryNavigationItemsSelector).pipe(map((items) => items?.filter((item) => item.path !== '/alerts-notifications/categories')));

    public availableLanguages$ = this.store.select(availableLanguagesSelector);

    get currentLang(): string {
        return this.translate.currentLang;
    }

    constructor(
        private translate: TranslateService,
        private store: Store<State>,
        private dialog: MatDialog,
        private reportContextService: ReportContextService,
    ) {}

    ngOnInit(): void {
        this.translate.onLangChange
            .pipe(
                takeUntil(this.destroy$),
                tap(() => this.store.dispatch(GetNavigationAction())),
            )
            .subscribe();
    }

    openReportProblemDialog(): void {
        if (this.dialog.getDialogById(REPORT_PROBLEM_DIALOG_ID)) {
            return;
        }

        // The context is captured before opening, so it describes the page the problem happened on.
        this.reportContextService
            .snapshot()
            .pipe(takeUntil(this.destroy$))
            .subscribe((snapshot) => {
                this.dialog.open<ReportProblemDialogComponent, ReportProblemDialogData>(ReportProblemDialogComponent, {
                    id: REPORT_PROBLEM_DIALOG_ID,
                    data: { snapshot },
                    panelClass: 'no-padding-dialog',
                    width: '560px',
                    maxWidth: 'calc(100vw - 32px)',
                    autoFocus: false,
                    disableClose: true,
                    ariaLabelledBy: 'report-problem-title',
                });
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    toggleMenu(_events: any): void {
        this.menuOpen = !this.menuOpen;
    }

    selectLanguage(language: string): void {
        this.store.dispatch(SetLanguageAction({ language }));
        this.store.dispatch(SaveLanguageAction({ language }));
    }
}
